import { storage } from '@/lib/storage';
import { Vehicle } from '@/types';
import { nanoid } from 'nanoid';

const VEHICLES_KEY = 'vehicles';

const normalizePlate = (value: string) => value.trim().toUpperCase();

export const vehicleService = {
  getAll: (): Vehicle[] => {
    return storage.get<Vehicle[]>(VEHICLES_KEY) || [];
  },

  getById: (id: string): Vehicle | null => {
    return vehicleService.getAll().find((vehicle) => vehicle.id === id) || null;
  },

  getByCustomerId: (customerId: string): Vehicle[] => {
    return vehicleService
      .getAll()
      .filter((vehicle) => vehicle.customer_id === customerId && vehicle.is_active);
  },

  getLabel: (vehicle: Vehicle): string => {
    const model = [vehicle.make, vehicle.model].filter(Boolean).join(' ');
    return [vehicle.plate_number, model].filter(Boolean).join(' - ');
  },

  search: (query: string): Vehicle[] => {
    const q = query.toLowerCase().trim();
    if (!q) return vehicleService.getAll();

    return vehicleService.getAll().filter((vehicle) => {
      const fields = [
        vehicle.plate_number,
        vehicle.customer_name,
        vehicle.make,
        vehicle.model,
        vehicle.tire_size,
        vehicle.oil_type,
      ];

      return fields.some((field) => field?.toLowerCase().includes(q));
    });
  },

  create: (
    data: Omit<Vehicle, 'id' | 'created_at' | 'updated_at' | 'is_active'>
  ): Vehicle => {
    const vehicles = vehicleService.getAll();
    const newVehicle: Vehicle = {
      ...data,
      plate_number: normalizePlate(data.plate_number),
      id: nanoid(),
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    vehicles.push(newVehicle);
    storage.set(VEHICLES_KEY, vehicles);
    return newVehicle;
  },

  update: (id: string, data: Partial<Vehicle>): Vehicle | null => {
    const vehicles = vehicleService.getAll();
    const index = vehicles.findIndex((vehicle) => vehicle.id === id);
    if (index === -1) return null;

    vehicles[index] = {
      ...vehicles[index],
      ...data,
      plate_number: data.plate_number
        ? normalizePlate(data.plate_number)
        : vehicles[index].plate_number,
      id: vehicles[index].id,
      created_at: vehicles[index].created_at,
      updated_at: new Date().toISOString(),
    };

    storage.set(VEHICLES_KEY, vehicles);
    return vehicles[index];
  },

  delete: (id: string): boolean => {
    const vehicles = vehicleService.getAll();
    const next = vehicles.filter((vehicle) => vehicle.id !== id);
    if (next.length === vehicles.length) return false;
    storage.set(VEHICLES_KEY, next);
    return true;
  },
};
